import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { INTERACTION, LAYER_TYPE } from '@tmapy/config';
import { useMessage } from '@tmapy/intl';
import { actionSetLayerOpacity, actionSetLayerVisibility, actionSetInteraction, actionSetSnapInteraction, } from '@tmapy/mapcore';
import { useLink, useLocation } from '@tmapy/router';
import { SvgLine, SvgPoint, SvgEyeOff, SvgMagnet, SvgTable, Icon, Checkbox, OpacitySlider, TertiaryBtn, SIZE, TertiaryLink, SvgPolygon, } from '@tmapy/style-guide';
import './LayerItem.scss';
import { layerSwitcherMessages } from './layerSwitcherMessages';
import { actionMakeLayersVisible } from '../actions';
const IconByDrawGeometryType = {
    Point: SvgPoint,
    LineString: SvgLine,
    Polygon: SvgPolygon,
};
export const LayerItem = ({ layer, layers, routes, resolution, interaction, dispatch, }) => {
    const formatMessage = useMessage();
    const { layerId, exclusiveLayerIds, drawGeometryType } = layer;
    const mapLayer = layers.find((mapLayer) => mapLayer.id === layer.layerId);
    const opacity = mapLayer?.layerOptions.opacity;
    const snap = interaction.snap;
    const isOnCreateRoute = routes.currentRouteId === layer.createRouteId;
    const isOnTableRoute = routes.currentRouteId === layer.routeId;
    const createRouteId = (!layer.createRouteId || isOnCreateRoute ? routes.mapRouteId : layer.createRouteId) || null;
    const tableRouteId = (!layer.routeId || isOnTableRoute ? routes.mapRouteId : layer.routeId) || null;
    const location = useLocation();
    const createRouteLink = useLink(createRouteId, location.params, undefined, 'push', true);
    const tableRouteLink = useLink(tableRouteId, location.params, undefined, 'push', true);
    const isZoomLevelInvisible = useMemo(() => {
        const layerOptions = mapLayer?.layerOptions;
        if (!resolution || !layerOptions)
            return false;
        const { minResolution, maxResolution } = layerOptions;
        const lowBoundCond = minResolution ? minResolution <= resolution : true;
        const highBoundCond = maxResolution ? resolution <= maxResolution : true;
        if (lowBoundCond && highBoundCond)
            return false;
        return true;
    }, [resolution, mapLayer]);
    const handleCheckboxChange = useCallback((e) => {
        const value = e.target.checked;
        const layerId = e.target.name;
        if (value) {
            dispatch(actionMakeLayersVisible([layerId]));
        }
        else {
            dispatch(actionSetLayerVisibility({ [layerId]: false }));
        }
        // check and disable edit after deactivate visibility layer
        if (!value &&
            interaction.type === INTERACTION.DRAW &&
            interaction.originLayerId === layerId) {
            dispatch(actionSetInteraction({ type: INTERACTION.NONE, originLayerId: layerId }, { activateDrawTool: false }));
        }
    }, [dispatch, interaction.originLayerId, interaction.type]);
    const handleOpacitySliderChange = useCallback((e, eventData) => {
        const opacity = eventData.value;
        const visibilityLayers = { [layerId]: !!opacity };
        if (opacity && exclusiveLayerIds) {
            exclusiveLayerIds.forEach((exclusiveLayerId) => (visibilityLayers[exclusiveLayerId] = false));
        }
        dispatch(actionSetLayerOpacity({ [layerId]: opacity }));
        dispatch(actionSetLayerVisibility(visibilityLayers));
    }, [dispatch, layerId, exclusiveLayerIds]);
    const sliderOpacity = opacity ?? 1;
    // Pokud je snap zapnuty na vrstve, ktera neni videt, tak nema zadny efekt
    const isSnapEnabled = !!mapLayer?.layerOptions.visible && snap.layerIds.includes(layerId);
    const handleSnapBtnClick = useCallback(() => {
        !isSnapEnabled &&
            handleCheckboxChange({
                target: {
                    checked: true,
                    name: layerId,
                },
            });
        dispatch(actionSetSnapInteraction(isSnapEnabled
            ? { layerIds: snap.layerIds.filter((id) => id !== layerId) }
            : { layerIds: [...snap.layerIds, layerId] }));
    }, [dispatch, handleCheckboxChange, isSnapEnabled, layerId, snap.layerIds]);
    let drawButton = null;
    if (mapLayer?.type === LAYER_TYPE.TILE_VECTOR &&
        mapLayer?.canInsert &&
        drawGeometryType &&
        layer.createRouteId) {
        const GeometryIcon = IconByDrawGeometryType[drawGeometryType];
        if (!GeometryIcon) {
            throw new Error('Unsupported geometry type.');
        }
        drawButton = (_jsx(TertiaryLink, { ...createRouteLink, isSuppressed: true, isActive: isOnCreateRoute, icon: { element: _jsx(GeometryIcon, {}) }, size: SIZE.SML, tooltip: formatMessage(layerSwitcherMessages.drawBtn) }));
    }
    let snapButton = null;
    if (drawGeometryType) {
        snapButton = (_jsx(TertiaryBtn, { isSuppressed: true, isActive: isSnapEnabled, icon: { element: _jsx(SvgMagnet, {}) }, size: SIZE.SML, tooltip: formatMessage(layerSwitcherMessages.snapBtn), onClick: handleSnapBtnClick }));
    }
    let tableDataButton = null;
    if (tableRouteLink?.href && layer.routeId) {
        tableDataButton = (_jsx(TertiaryLink, { isSuppressed: true, isActive: isOnTableRoute, size: SIZE.SML, icon: { element: _jsx(SvgTable, {}) }, tooltip: formatMessage(layerSwitcherMessages.routeBtn), ...tableRouteLink }));
    }
    return (_jsx(_Fragment, { children: mapLayer && (_jsxs("div", { className: classNames('lsw-layerItem', {
                'lsw-layerItem-hasSlider': layer.isOpacitySliderVisible,
                'lsw-layerItem-isZoomLevelInvisible': isZoomLevelInvisible,
            }), "data-icon-btn-hover": true, children: [_jsxs("div", { className: 'lsw-layerItem--inner', children: [_jsx(Checkbox, { isChecked: mapLayer.layerOptions.visible, onChange: handleCheckboxChange, name: layer.layerId, isExclusive: layer.showExclusivity, children: _jsxs("div", { className: 'lsw-layerItem--label', children: [_jsx("span", { className: 'lsw-layerItem--labelInner', children: layer.label }), _jsxs("div", { className: 'lsw-layerItem--tools', children: [drawButton, snapButton, tableDataButton] })] }) }), layer.isOpacitySliderVisible && (_jsxs("div", { className: 'lsw-layerItem--sliderWrapper sg-a-d-f sg-a-ai-c', children: [_jsxs("div", { className: 'lsw-layerItem--sliderValue', children: [Math.floor(sliderOpacity * 100), " %"] }), _jsx("div", { className: 'lsw-layerItem--slider sg-a-f-a', children: _jsx(OpacitySlider, { value: sliderOpacity, showValue: false, onChange: handleOpacitySliderChange }) })] }))] }), _jsx("div", { className: 'lsw-layerItem--invisibleIcon', title: formatMessage(layerSwitcherMessages.zoomInvisibleLayer), children: _jsx(Icon, { element: _jsx(SvgEyeOff, {}) }) })] })) }));
};
//# sourceMappingURL=LayerItem.js.map